// Browser registry loader (client adapter for ARCH §3.3). Vite's import.meta.glob
// pulls every data/**/*.json into the bundle; the pure sim loader interns them.
// The Node/server/test side reads the same files via tools/read-data.ts.
import { loadRegistries, type Registries } from '../sim/registry/load';
import { applyKind } from '../sim/registry/overlay';
import { loadOverlay } from './storage/overlay';

const asArray = (mods: Record<string, unknown>): unknown[] => Object.values(mods);

export function loadClientRegistries(): Registries {
  const blocks = import.meta.glob('/data/blocks/**/*.json', { eager: true, import: 'default' });
  const items = import.meta.glob('/data/items/**/*.json', { eager: true, import: 'default' });
  const loot = import.meta.glob('/data/loot/**/*.json', { eager: true, import: 'default' });
  const recipes = import.meta.glob('/data/recipes/**/*.json', { eager: true, import: 'default' });
  const soundFiles = import.meta.glob('/data/sounds/*.json', { eager: true, import: 'default' });
  const sounds = asArray(soundFiles).flatMap((f) => (Array.isArray(f) ? f : [f]));
  const biomeFiles = import.meta.glob('/data/biomes/*.json', { eager: true, import: 'default' }) as Record<string, unknown>;
  const featureFiles = import.meta.glob('/data/features/*.json', { eager: true, import: 'default' }) as Record<string, unknown>;
  const mobs = import.meta.glob('/data/mobs/**/*.json', { eager: true, import: 'default' });
  const enchantments = import.meta.glob('/data/enchantments/**/*.json', { eager: true, import: 'default' });
  const trades = import.meta.glob('/data/trades/**/*.json', { eager: true, import: 'default' });
  const raids = import.meta.glob('/data/raids/**/*.json', { eager: true, import: 'default' });
  // Lessons never ship as `default` content (D-TF-112/143) — they only ever arrive
  // through an imported pack's overlay patch, so there is no `/data/lessons/` glob.
  // Merge every data/features/*.json (ores, vegetation, …) into one record — the
  // same shape tools/read-data.ts builds for the Node/test side.
  const features: Record<string, unknown> = {};
  for (const obj of Object.values(featureFiles)) Object.assign(features, obj as Record<string, unknown>);
  // The editor's saved overlay patches the shipped data on the way in, so an
  // edited item/mob/block is live everywhere the registry is read.
  const { patches } = loadOverlay();
  const build = (withOverlay: boolean): Registries => loadRegistries({
    blocks: applyKind(asArray(blocks), withOverlay ? patches.blocks : undefined),
    items: applyKind(asArray(items), withOverlay ? patches.items : undefined),
    loot: asArray(loot),
    recipes: applyKind(asArray(recipes), withOverlay ? patches.recipes : undefined),
    sounds,
    biomes: biomeFiles['/data/biomes/biomes.json'] as unknown[] | undefined,
    placement: biomeFiles['/data/biomes/placement.json'] as unknown[] | undefined,
    spline: biomeFiles['/data/biomes/terrain-spline.json'],
    features: Object.keys(features).length ? features : undefined,
    mobs: applyKind(asArray(mobs), withOverlay ? patches.mobs : undefined),
    enchantments: asArray(enchantments),
    trades: asArray(trades),
    raids: asArray(raids),
    lessons: applyKind([], withOverlay ? patches.lessons : undefined),
  });

  // An overlay that no longer validates (hand-edited, or written by an older
  // build whose schema has since changed) must not brick the game: fall back to
  // the shipped content and say so, rather than failing to boot at all.
  try {
    return build(true);
  } catch (err) {
    console.error('content overlay rejected — loading shipped data instead:', err);
    return build(false);
  }
}
